<template>
  <Dialog ref="dialog"
          width="45%"
          :center="true"
          icon="fas fa-file-excel"
          @confirm="processExcel"
          :show-confirm="!isProcessing && excelFile !== null"
          title="Subir archivo Excel">
    <div v-if="!isProcessing">
      <div class="upload-section">
        <el-upload
          ref="upload"
          class="upload-demo"
          drag
          :auto-upload="false"
          :on-change="handleFileChange"
          :file-list="fileList"
          :limit="1"
          accept=".xlsx,.xls"
          :before-remove="handleRemove">
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">
            Arrastra el Excel aquí o <em>haz clic</em>
          </div>
          <div class="el-upload__tip" slot="tip">
            Excel con 2 columnas: A (subórdenes/LPNs) y B (sectores/skills)
          </div>
        </el-upload>
      </div>
      <div v-if="excelFile" class="file-info">
        <p><strong>Archivo seleccionado:</strong> {{ excelFile.name }}</p>
        <p><strong>Tamaño:</strong> {{ formatFileSize(excelFile.size) }}</p>
      </div>
      <div class="verify-version-option">
        <el-checkbox v-model="useVerifyV2">
          USAR ENDPOINT DE VERIFICACIÓN V2
        </el-checkbox>
        <span class="verify-version-help">/v1/planning/suborders/verify/v2</span>
      </div>
    </div>
    <div v-else class="progress">
      <p class="indicator">
        <span>Total a procesar: </span>{{ totalToProcess }}
      </p>
      <el-progress :color="color"
                   :text-inside="true"
                   :stroke-width="20"
                   :percentage="loadPercentage"/>
      <p class="indicators">
        <i :class="loadIcon"/> {{ msgLoad }}
        <span class="in-load">
          Procesadas: <span class="verified">{{ countProcessed }}</span> /
          Errores: <span class="not-found">{{ countErrors }}</span>
        </span>
      </p>
    </div>
  </Dialog>
</template>

<script>
import XLSX from 'xlsx';
import Dialog from '../../base/Dialog.vue';
import { apiRequest } from '../../../../backend/vue-axios/axios';

export default {
  name: 'UploadExcel',
  components: {
    Dialog,
  },
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    sectors: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      excelFile: null,
      fileList: [],
      isProcessing: false,
      msgLoad: 'Procesando archivo...',
      totalToProcess: 0,
      countProcessed: 0,
      countErrors: 0,
      color: '#AAD500',
      list: this.value,
      useVerifyV2: false,
    };
  },
  watch: {
    list() {
      this.$emit('input', this.list);
    },
    value() {
      this.list = this.value;
    },
  },
  methods: {
    open() {
      this.resetDialog();
      this.$refs.dialog.visible(true);
    },
    resetDialog() {
      this.excelFile = null;
      this.fileList = [];
      this.isProcessing = false;
      this.msgLoad = 'Procesando archivo...';
      this.totalToProcess = 0;
      this.countProcessed = 0;
      this.countErrors = 0;
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles();
      }
    },
    handleFileChange(file) {
      this.excelFile = file.raw;
      this.fileList = [file];
    },
    handleRemove() {
      this.excelFile = null;
      this.fileList = [];
      return true;
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    },
    async processExcel() {
      if (!this.excelFile) {
        this.$notify.error({ title: 'Error', message: 'Por favor selecciona un archivo Excel' });
        return;
      }

      this.isProcessing = true;

              try {
          const data = await this.readExcelFile(this.excelFile);
          const { suborders, lpns } = this.extractData(data);

          if (suborders.length === 0 && lpns.length === 0) {
            this.$notify.error({
              title: 'Error',
              message: 'No se encontraron números de subórdenes o LPNs válidos en el archivo',
            });
            this.isProcessing = false;
            return;
          }

          this.totalToProcess = suborders.length + lpns.length;
          await this.verifyData(suborders, lpns);
      } catch (error) {
        this.$notify.error({
          title: 'Error',
          message: `Error al procesar el archivo Excel: ${error.message}`,
        });
        this.isProcessing = false;
      }
    },
    readExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            resolve(jsonData);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Error al leer el archivo'));
        reader.readAsArrayBuffer(file);
      });
    },
    extractData(data) {
      const suborders = [];
      const lpns = [];

      // Procesar cada fila del Excel (Columna A: suborden/LPN, Columna B: sector/skill)
      data.forEach((row, index) => {
        if (Array.isArray(row) && row.length >= 1) {
          const cellA = row[0]; // Columna A - Suborden/LPN
          const cellB = row[1]; // Columna B - Sector/Skill (puede estar vacía)

          // Validar que la columna A contenga un valor válido
          if (cellA && (typeof cellA === 'string' || typeof cellA === 'number')) {
            const valueStr = cellA.toString().trim();
            const dataType = this.detectDataType(valueStr);
            
            if (dataType === 'suborder') {
              // Es una suborden
              const suborder = parseInt(valueStr, 10);
              
              // Verificar que no esté duplicado
              const exists = suborders.find(s => s.suborder === suborder);
              if (!exists) {
                const suborderObj = {
                  suborder: suborder,
                };
                
                // Solo agregar sector si la columna B tiene valor
                if (cellB && cellB.toString().trim()) {
                  suborderObj.sector = cellB.toString().trim();
                }
                
                suborders.push(suborderObj);
              }
            } else if (dataType === 'lpn') {
              // Es un LPN
              // Verificar que no esté duplicado
              const exists = lpns.find(l => l.lpn === valueStr);
              if (!exists) {
                const lpnObj = {
                  lpn: valueStr,
                };
                
                // Solo agregar sector si la columna B tiene valor
                if (cellB && cellB.toString().trim()) {
                  lpnObj.sector = cellB.toString().trim();
                }
                
                lpns.push(lpnObj);
              }
            }
            // Si es 'invalid', simplemente lo ignoramos
          }
        }
      });

      console.log('📈 Resumen de extracción:');
      console.log('  🔢 Subórdenes encontradas:', suborders.length);
      console.log('  📦 LPNs encontrados:', lpns.length);

      return { suborders, lpns };
    },
    
    // Método para detectar automáticamente el tipo de dato
    detectDataType(value) {
      const cleanValue = value.toString().trim();
      
      // PRIMERO: Es LPN si cumple patrones específicos conocidos
      if (/^(700000000000|316400000000|140111000002)[0-9]+$/.test(cleanValue)) {
        return 'lpn';
      }
      
      // SEGUNDO: Es LPN si: más de 20 dígitos
      if (/^[0-9]{21,}$/.test(cleanValue)) {
        return 'lpn';
      }
      
      // TERCERO: Es suborden si: 6-20 dígitos (después de verificar LPNs)
      if (/^[0-9]{6,20}$/.test(cleanValue)) {
        return 'suborder';
      }
      
      // No válido
      return 'invalid';
    },
    
    // Método para validar si es una suborden
    isSuborder(value) {
      return this.detectDataType(value) === 'suborder';
    },
    
    // Método para validar si es un LPN
    isLPN(value) {
      return this.detectDataType(value) === 'lpn';
    },
    
    // Método principal para verificar ambos tipos de datos
    async verifyData(suborders, lpns) {
      try {
        this.msgLoad = 'Verificando subórdenes y LPNs con el servidor...';
        this.countProcessed = 0;
        this.countErrors = 0;

        // Filtrar subórdenes que no estén ya en la lista
        const existingSuborders = this.list.map(l => l.suborder).filter(s => s);
        const newSocs = suborders.filter(s => !existingSuborders.includes(s.suborder));

        // Filtrar LPNs que no estén ya en la lista
        const existingLPNs = this.list.map(l => l.lpn).filter(l => l);
        const newLPNs = lpns.filter(l => !existingLPNs.includes(l.lpn));

        console.log('🔍 Procesando:', newSocs.length, 'subórdenes y', newLPNs.length, 'LPNs');

        if (newSocs.length === 0 && newLPNs.length === 0) {
          this.$notify.warning({
            title: 'Información',
            message: 'Todas las subórdenes y LPNs ya están cargadas',
          });
          this.isProcessing = false;
          return;
        }

        // Procesar subórdenes y LPNs en paralelo
        const promises = [];
        
        if (newSocs.length > 0) {
          promises.push(this.verifySubordersOnly(newSocs));
        }
        
        if (newLPNs.length > 0) {
          promises.push(this.verifyLPNs(newLPNs));
        }

        // Esperar a que terminen ambos procesos
        await Promise.all(promises);

        // Mostrar mensaje final
        this.msgLoad = 'Procesamiento completado';
        
        const totalSuccess = (newSocs.length + newLPNs.length) - this.countErrors;
        
        this.$notify.success({
          title: 'Éxito',
          message: `Proceso completado: ${totalSuccess} elementos verificados, ${this.countErrors} errores`,
        });

        setTimeout(() => {
          this.$refs.dialog.visible(false);
          this.resetDialog();
        }, 2000);

      } catch (error) {
        this.$notify.error({
          title: 'Error',
          message: `Error al verificar datos: ${error.message || 'Error desconocido'}`,
        });
        this.isProcessing = false;
      }
    },
    
    // Método específico para verificar solo subórdenes
    async verifySubordersOnly(newSocs) {
      try {
        const suborderNumbers = newSocs.map(s => s.suborder);

        const response = await apiRequest({
          module: 'planning',
          service: this.useVerifyV2 ? 'verify_suborders_v2' : 'verify_suborders',
          params: {
            suborders: suborderNumbers,
            with_splits: false,
          },
        });

        const verifiedSuborders = response.data || [];
        this.processVerifiedSuborders(verifiedSuborders, newSocs);
      } catch (error) {
        this.countErrors += newSocs.length;
        console.error('❌ Error verificando subórdenes:', error);
      }
    },
    
    // Método específico para verificar LPNs
    async verifyLPNs(newLPNs) {
      try {
        const lpnNumbers = newLPNs.map(l => l.lpn);

        const response = await apiRequest({
          module: 'planning',
          service: 'verify_lpn',
          params: {
            lpn_list: lpnNumbers,
            with_splits: false,
          },
        });

        const verifiedLPNs = response.data || [];
        this.processVerifiedLPNs(verifiedLPNs, newLPNs);
      } catch (error) {
        this.countErrors += newLPNs.length;
        console.error('❌ Error verificando LPNs:', error);
      }
    },
    
    // Método para procesar LPNs verificados
    processVerifiedLPNs(verifiedLPNs, originalLPNs) {
      // Crear objetos de LPNs con los datos del servidor y del Excel
      const newLPNsWithData = verifiedLPNs.map(lpn => {
        // Encontrar el sector correspondiente del Excel
        const excelData = originalLPNs.find(l => l.lpn === lpn.lpn);
        const excelSector = excelData ? excelData.sector : '';
        
        return {
          lpn: lpn.lpn,
          suborder: lpn.suborder, // LPN viene con suborden asociada
          verified: true,
          sectors: this.sectors.filter((sector) => {
            return (sector.packageTypes.map(pt => pt.id).includes(lpn.package_type_id)
                || (sector.packageTypes.length > 0 && lpn.package_type_name === 'NF'))
                && sector.localities.map(l => l.id).includes(lpn.locality_id)
                && !(lpn.splits || [5, 6].includes(lpn.last_state));
          }),
          source: 'excel',
          address: lpn.address || '',
          coordinates: lpn.coordinates || null,
          excelSector: excelSector, // Sector/skill del Excel
          ...lpn,
        };
      });

      // Identificar LPNs que no fueron encontradas en el servidor
      const verifiedIds = verifiedLPNs.map(l => l.lpn);
      const notFoundLPNs = originalLPNs
        .filter(l => !verifiedIds.includes(l.lpn))
        .map(l => ({
          lpn: l.lpn,
          suborder: 0,
          verified: false,
          sectors: [],
          source: 'excel',
          error: 'No encontrada en el sistema',
          excelSector: l.sector, // Sector/skill del Excel
        }));

      this.countErrors += notFoundLPNs.length;

      // Agregar todas las LPNs a la lista
      this.list = [...newLPNsWithData, ...notFoundLPNs, ...this.list];
      
      // Simular progreso para LPNs
      const totalLPNs = newLPNsWithData.length + notFoundLPNs.length;
      this.countProcessed += totalLPNs;
    },
    processVerifiedSuborders(verifiedSuborders, originalSuborders) {
      // Crear objetos de subórdenes con los datos del servidor y del Excel
      const newSubordersWithData = verifiedSuborders.map(suborder => {
        // Encontrar el sector correspondiente del Excel
        const excelData = originalSuborders.find(s => s.suborder === suborder.suborder);
        const excelSector = excelData ? excelData.sector : '';
        
        return {
          suborder: suborder.suborder,
          verified: true,
          sectors: suborder.sectors || [],
          source: 'excel',
          address: suborder.address || '',
          coordinates: suborder.coordinates || null,
          loadedWithVerifyV2: this.useVerifyV2,
          excelSector: excelSector, // Sector/skill del Excel
          ...suborder,
        };
      });

      // Identificar subórdenes que no fueron encontradas en el servidor
      const verifiedIds = verifiedSuborders.map(s => s.suborder);
      const notFoundSuborders = originalSuborders
        .filter(s => !verifiedIds.includes(s.suborder))
        .map(s => ({
          suborder: s.suborder,
          verified: false,
          sectors: [],
          source: 'excel',
          error: 'No encontrada en el sistema',
          loadedWithVerifyV2: this.useVerifyV2,
          excelSector: s.sector, // Sector/skill del Excel
        }));

      this.countErrors += notFoundSuborders.length;

      // Agregar todas las subórdenes a la lista
      this.list = [...newSubordersWithData, ...notFoundSuborders, ...this.list];
      
      // Actualizar progreso para subórdenes
      const totalSuborders = newSubordersWithData.length + notFoundSuborders.length;
      this.countProcessed += totalSuborders;
    },
  },
  computed: {
    loadPercentage() {
      return this.totalToProcess > 0
        ? Math.round((this.countProcessed / this.totalToProcess) * 100) : 0;
    },
    loadIcon() {
      return this.loadPercentage === 100 ? 'el-icon-folder-checked' : 'el-icon-loading';
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-section {
  margin-bottom: 15px;
  text-align: center;

  .upload-demo {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    
    .el-upload-dragger {
      width: 80%;
      max-width: 400px;
      border: 2px dashed #d9d9d9;
      border-radius: 6px;
      text-align: center;
      padding: 25px;

      &:hover {
        border-color: #409EFF;
      }

      .el-icon-upload {
        font-size: 45px;
        color: #C0C4CC;
        margin-bottom: 10px;
      }

      .el-upload__text {
        color: #606266;
        font-size: 14px;

        em {
          color: #409EFF;
          font-style: normal;
        }
      }
    }
  }

  .el-upload__tip {
    color: #606266;
    font-size: 12px;
    margin-top: 7px;
    text-align: center;
  }
}

.file-info {
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 10px;
  margin-top: 8px;

  p {
    margin: 5px 0;
    font-size: 13px;
    color: #606266;
  }
}

.verify-version-option {
  border-top: 1px solid #E4E7ED;
  margin-top: 12px;
  padding-top: 12px;

  .verify-version-help {
    color: #909399;
    display: block;
    font-size: 11px;
    margin: 5px 0 0 24px;
  }
}

.progress {
  margin: 14px;

  .indicators {
    height: 18px;
    font-size: 13px;
    margin: 4px 2px;
    font-weight: 300;
    text-transform: lowercase;

    i {
      color: $fb-green;
      font-weight: 900;
      margin-right: 3px;
    }

    .in-load {
      float: right;

      .verified {
        color: $fb-blue;
        font-weight: 600;
      }

      .not-found {
        color: $fb-red;
        font-weight: 600;
      }
    }
  }

  .indicator {
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 4px 3px;
    text-transform: lowercase;

    span {
      font-weight: 300;
    }
  }
}
</style>